import React, { useState } from 'react';
import ProductCard from './ProductCard';
import { Product } from '../types';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface ProductGridProps {
  products: Product[];
  onAddToCart?: (productId: number) => void;
  loading?: boolean;
  title?: string;
  titleClassName?: string;
}

const ProductGrid: React.FC<ProductGridProps> = ({ 
  products, 
  onAddToCart, 
  loading = false, 
  title = "Productos",
  titleClassName = "text-3xl font-bold text-gray-900 mb-8 text-center"
}) => {
  const itemsPerPageMobile = 6;
  const [currentPage, setCurrentPage] = useState(1);
  
  // Calcular productos para la página actual (solo móvil)
  const totalPagesMobile = Math.ceil(products.length / itemsPerPageMobile);
  const startIndex = (currentPage - 1) * itemsPerPageMobile;
  const endIndex = startIndex + itemsPerPageMobile;
  const currentProducts = products.slice(startIndex, endIndex);

  // Generar números de página para mostrar
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    
    if (totalPagesMobile <= maxPagesToShow) {
      for (let i = 1; i <= totalPagesMobile; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPagesMobile);
      } else if (currentPage >= totalPagesMobile - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPagesMobile - 3; i <= totalPagesMobile; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('...');
        pages.push(totalPagesMobile);
      }
    }
    
    return pages;
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h2 className={titleClassName}>{title}</h2>
        {/* Desktop skeleton */}
        <div className="hidden md:grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[...Array(8)].map((_, index) => (
            <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden animate-pulse">
              <div className="h-64 bg-gray-300"></div>
              <div className="p-4">
                <div className="h-4 bg-gray-300 rounded mb-2"></div>
                <div className="h-6 bg-gray-300 rounded mb-2"></div>
                <div className="h-4 bg-gray-300 rounded mb-4"></div>
                <div className="h-8 bg-gray-300 rounded"></div>
              </div>
            </div>
          ))}
        </div>
        {/* Mobile skeleton */}
        <div className="md:hidden grid grid-cols-2 gap-0">
          {[...Array(6)].map((_, index) => (
            <div key={index} className="aspect-square bg-gray-300 animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h2 className={titleClassName}>{title}</h2>
        <div className="text-center py-12">
          <span className="material-icons text-6xl text-gray-400 mb-4">inventory_2</span>
          <p className="text-gray-600 text-lg">No hay productos disponibles en este momento.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-0 md:px-4 sm:px-6 lg:px-8 py-12">
      <h2 className={titleClassName}>{title}</h2>
      
      {/* Desktop Grid - con espaciado */}
      <div className="hidden md:grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 px-4">
        {products.map((product) => (
          <ProductCard
            key={product.id}
            product={product}
            onAddToCart={onAddToCart}
          />
        ))}
      </div>

      {/* Mobile Grid - sin espaciado, con paginación */}
      <div className="md:hidden">
        <div className="grid grid-cols-2 gap-0">
          {currentProducts.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              onAddToCart={onAddToCart}
            />
          ))}
        </div>

        {/* Paginación móvil */}
        {totalPagesMobile > 1 && (
          <div className="mt-8 px-4">
            {/* Botón "Cargar siguiente página" */}
            {currentPage < totalPagesMobile && (
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                className="w-full bg-black text-white py-3 px-6 mb-4 hover:bg-gray-800 transition-colors"
              >
                CARGAR SIGUIENTE PÁGINA
              </button>
            )}

            {/* Números de página */}
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="p-2 disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Página anterior"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              {getPageNumbers().map((page, index) => (
                <React.Fragment key={index}>
                  {page === '...' ? (
                    <span className="px-2 text-gray-500">...</span>
                  ) : (
                    <button
                      onClick={() => handlePageChange(page as number)}
                      className={`min-w-[40px] h-10 px-3 ${
                        currentPage === page
                          ? 'bg-black text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-100'
                      } transition-colors`}
                    >
                      {page}
                    </button>
                  )}
                </React.Fragment>
              ))}

              <button
                onClick={() => handlePageChange(Math.min(totalPagesMobile, currentPage + 1))}
                disabled={currentPage === totalPagesMobile}
                className="p-2 disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Página siguiente"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductGrid;